// Renders nothing. Reports react-router navigation and the authenticated
// account to UXCam, neither of which the SDK can infer on its own.

import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from 'components/auth'
import { getAccountIdentifier, USER_METADATA } from 'config'
import { isAnalyticsAllowed, onAnalyticsConsentChange } from 'consent-service'
import { logEvent, setUserIdentity, setUserProperties } from 'uxcam-service'
import { flowStepCount, resolvePage } from 'uxcam-pages'

export const EVENT = {
  FLOW_STEP_VIEWED: 'flow_step_viewed',
  PAGE_VIEWED: 'page_viewed',
}

// CookieYes answers after the bundle runs, so consent is usually denied on the
// first render. Re-rendering when it lands lets the effects below retry.
function useAnalyticsAllowed() {
  const [allowed, setAllowed] = useState(isAnalyticsAllowed)

  useEffect(() => onAnalyticsConsentChange(setAllowed), [])

  return allowed
}

function UxCamTracker() {
  const { pathname } = useLocation()
  const { isAuthenticated, user } = useAuth()
  const analyticsAllowed = useAnalyticsAllowed()
  const lastTrackedPathname = useRef(null)
  const lastIdentifiedAccount = useRef(null)

  // Keyed on pathname only: a query-string change isn't a new page, and firing
  // on every one would make funnel step counts meaningless.
  useEffect(() => {
    // Before the ref: marking it tracked while calls are still dropped would
    // lose the entry page for good. Earlier pages stay unreported by design.
    // Cleared on withdrawal so a re-grant starts a new session with this page.
    if (!analyticsAllowed) {
      lastTrackedPathname.current = null
      return
    }
    if (lastTrackedPathname.current === pathname) return
    lastTrackedPathname.current = pathname

    const { name, flow, step, isMapped } = resolvePage(pathname)

    logEvent(EVENT.PAGE_VIEWED, {
      page: name,
      flow,
      flow_step: step,
      // Surfaces a route missing from `uxcam-pages` instead of dropping it.
      unmapped: isMapped ? undefined : true,
    })

    if (!flow) return

    logEvent(EVENT.FLOW_STEP_VIEWED, {
      flow,
      step,
      step_count: flowStepCount(flow),
      page: name,
    })
  }, [analyticsAllowed, pathname])

  // `user` arrives after the first render, and the identity only needs sending
  // once per account.
  useEffect(() => {
    // Before the ref, same as above.
    if (!analyticsAllowed) {
      lastIdentifiedAccount.current = null
      return
    }
    if (!isAuthenticated || !user) return

    const accountId = getAccountIdentifier(user)
    if (!accountId || lastIdentifiedAccount.current === accountId) return
    lastIdentifiedAccount.current = accountId

    setUserIdentity(accountId)
    setUserProperties({
      account_id: accountId,
      auth_strategy: user[USER_METADATA.STRATEGY],
      is_partner: !!user[USER_METADATA.PARTNER_ACCOUNT_ID],
    })
  }, [analyticsAllowed, isAuthenticated, user])

  return null
}

export default UxCamTracker
